import {LabelStore, Language} from '../base/label-store';
import {ApiRepository} from '../base/api-repository';
import {action, makeObservable, observable} from 'mobx';
import {endOfMonth, startOfMonth, todayAsISOString} from '../base/dateUtils';
import {Datapoint} from '../dashboard/team-results-store';
import {ViewStore} from '../base/view-store';
import {AnswerContext, Channel, DeliveryTime, UserStore} from '../user/user-store';
import {SessionStore} from '../user/session-store';
import {routes} from '../base/routes';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {Capacitor} from '@capacitor/core';

export class ChatViewStore extends ViewStore {
    @observable
    payload?: ChatPayload = undefined;
    @observable
    variables: ChatVariables = new ChatVariables();

    constructor(private api: ApiRepository, labelStore: LabelStore, public userStore: UserStore, private sessionStore: SessionStore, private router: RouterStore<RootStore>) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onLogin(employeeId: string, token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            this.payload = await this.loadChat(employeeId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                history.replaceState({}, '', redirectUrl);
            }
        });
    }

    @action
    onEnter(): void {
        this.load(async () => {
            this.payload = await this.loadChat(this.userStore.payload!.employeeId);
            this.initializeVariables();
        });
    }

    @action
    onLoginAndAnswerQuestion(questionId: string, answer: '5' | '4' | '3' | '2' | '1' | 'text', token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            await this.submitAnswer(questionId, answer);
            this.payload = await this.loadQuestionChat(questionId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                history.replaceState({}, '', redirectUrl);
            }
        });
    }

    @action
    onLoginAndAskQuestion(questionId: string, token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            this.payload = await this.loadQuestionChat(questionId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                history.replaceState({}, '', redirectUrl);
            }
        });
    }

    @action
    onLoginOnboardingAndChooseLanguage(employeeId: string, language: Language, token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            await this.userStore.changeLanguage(language, employeeId);
            this.payload = await this.loadOnboarding(employeeId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                history.replaceState({}, '', redirectUrl);
            }
        });
    }

    @action
    onLoginWeeklyQuestions(employeeId: string, token?: string, tenantId?: string): void {
        this.initiateLogin(token, tenantId, async (): Promise<void> => {
            this.payload = await this.loadWeeklyQuestionsChat(employeeId);
            if (Capacitor.getPlatform() === 'web') {
                const redirectUrl = this.getRedirectUrl();

                history.replaceState({}, '', redirectUrl);
            }
        });
    }

    @action
    logout(): void {
        this.sessionStore.logout();
        this.userStore.clear();
        this.router.goTo(routes.dashboard);
    }

    @action
    onSubmitChoice(value: string, context?: AnswerContext): void {
        const type = this.variables.action!.type;

        if (type === 'ASK_QUESTION') {
            this.onSubmitAnswer(value as '5' | '4' | '3' | '2' | '1', context);
        }
        if (type === 'ASK_COMMENT') {
            this.onCommentOrDashboardOrUnansweredChoice(value as 'comment' | 'dashboard' | 'unanswered');
        }
        if (type === 'ASK_LANGUAGE') {
            this.onSubmitLanguage(value as Language);
        }
        if (type === 'ASK_DELIVERY_TIME') {
            this.onSubmitDeliveryTime(value as DeliveryTime);
        }
        if (type === 'ASK_CHANNEL') {
            this.onSubmitChannel(value as 'email' | 'android' | 'iphone' | 'slack');
        }
        if (type === 'ASK_ONBOARDING_CONSENT') {
            this.onSubmitOnboardingConsent(value as 'yes' | 'no');
        }
        if (type === 'ASK_QUESTION_FREQUENCY') {
            this.onSubmitQuestionFrequency(value as 'weekly' | 'one_every_day');
        }
        if (type === 'ASK_ANSWER_CONTEXT') {
            this.onSubmitAnswerContext(value as AnswerContext);
        }
    }

    @action
    onSubmitFreeTextChoice(value: string): void {
        const type = this.variables.action!.type;

        if (type === 'WRITE_COMMENT' && value === 'submit') {
            this.onFreeTextSubmit();
        }
        if (type === 'WRITE_COMMENT' && value === 'cancel') {
            this.onFreeTextCancel();
        }
        if (type === 'UPDATE_COMMENT' && value === 'submit') {
            this.onFreeTextUpdate();
        }
        if (type === 'UPDATE_COMMENT' && value === 'cancel') {
            this.onFreeTextUpdateCancel();
        }
    }

    @action
    onCommentOrDashboardOrUnansweredChoice(value: string): void {
        if (value === 'unanswered') {
            this.refreshChat(async (): Promise<void> => {
                const chatAction = this.variables.action! as MultipleChoiceAction;
                const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

                this.addEmployeeMessage(selectedChoice.text, 'green');
                await this.submitSkipComment(this.payload!.chatActionPayload.questionId!);
            });
        }
        if (value === 'comment') {
            this.addFreeTextAction();
        }
        if (value === 'dashboard') {
            this.routeToDashboard();
        }
    }

    @action
    async onSubmitLanguage(value: Language): Promise<void> {
        this.refreshChat(async (): Promise<void> => {
            const chatAction = this.variables.action! as MultipleChoiceAction;
            const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

            this.addEmployeeMessage(selectedChoice.text, 'green');
            await this.userStore.changeLanguage(value, this.payload!.employeeId);
        });
    }

    @action
    async onSubmitDeliveryTime(value: DeliveryTime): Promise<void> {
        this.refreshChat(async (): Promise<void> => {
            const chatAction = this.variables.action! as MultipleChoiceAction;
            const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

            this.addEmployeeMessage(selectedChoice.text, 'green');
            await this.userStore.changeDeliveryTime(value, this.payload!.employeeId);
        });
    }

    @action
    async onSubmitChannel(value: 'email' | 'android' | 'iphone' | 'slack'): Promise<void> {
        const chatAction = this.variables.action! as MultipleChoiceAction;
        const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

        if (value === 'email' || value === 'slack') {
            this.refreshChat(async (): Promise<void> => {
                this.addEmployeeMessage(selectedChoice.text, 'green');
                await this.userStore.changeChannel(value, this.payload!.employeeId);
            });
        } else {
            if (Capacitor.getPlatform() === 'web') {
                location.href = 'https://moticheck.com/app/';
            } else {
                this.addEmployeeMessage(selectedChoice.text, 'green');
                this.addBotMessage(this.label(`chat.choose-channel.${value}.redirect-to-store`));
                this.variables.action = undefined;
                this.deliverMessagesWithTimer();
                await new Promise((r) => setTimeout(r, 2000));

                location.href = value === 'android' ?
                    'https://play.google.com/store/apps/details?id=com.src.moticheck' :
                    'https://apps.apple.com/ee/app/moticheck/id1560352220';
            }
        }
    }

    @action
    async onSubmitOnboardingConsent(value: 'yes' | 'no'): Promise<void> {
        this.refreshChat(async (): Promise<void> => {
            const chatAction = this.variables.action! as MultipleChoiceAction;
            const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

            this.addEmployeeMessage(selectedChoice.text, selectedChoice.text === this.label('onboarding.yes') ? 'green' : 'red');
            await this.userStore.submitOnboardingConsent(value, this.payload!.employeeId);
        });
    }

    @action
    async onSubmitQuestionFrequency(value: 'weekly' | 'one_every_day'): Promise<void> {
        this.refreshChat(async (): Promise<void> => {
            const chatAction = this.variables.action! as MultipleChoiceAction;
            const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

            this.addEmployeeMessage(selectedChoice.text, 'green');
            await this.userStore.changeQuestionsFrequency(value, this.payload!.employeeId);
        });
    }

    @action
    async onSubmitAnswerContext(value: AnswerContext): Promise<void> {
        this.refreshChat(async (): Promise<void> => {
            const chatAction = this.variables.action! as MultipleChoiceAction;
            const selectedChoice = chatAction.choices.find((choice) => choice.value === value)!;

            this.addEmployeeMessage(selectedChoice.text, 'green');
            await this.userStore.changeAnswerContext(value, this.payload!.employeeId);
        });
    }

    @action
    onSubmitAnswer(answer: string, context?: AnswerContext): void {
        this.refreshChat(async (): Promise<void> => {
            this.addEmployeeMessage(this.getEmployeeMessageLabel(context, answer), this.getMessageColor(answer));
            if (this.payload!.chatActionPayload.questionType !== 'ADHOC_QUESTION') {
                this.addReaction(answer);
            }
            await this.submitAnswer(this.payload!.chatActionPayload.questionId!, answer, context);
        });
    }

    @action
    onFreeTextChange(value: string): void {
        const chatAction = this.variables.action! as FreeTextAction;

        chatAction.value = value;
    }

    @action
    onFreeTextSubmit(): void {
        this.refreshChat(async (): Promise<void> => {
            const text = (this.variables.action! as FreeTextAction).value.trim();

            if (text !== '') {
                this.addEmployeeMessage(text);
                await this.submitComment(this.payload!.chatActionPayload.questionId!, text);
            } else {
                this.addEmployeeMessage(this.label('chat.no-comment'));
                await this.submitSkipComment(this.payload!.chatActionPayload.questionId!);
            }
        });
    }

    @action
    onFreeTextCancel(): void {
        this.refreshChat(async (): Promise<void> => {
            const lastQuestion = this.payload!.questions[this.payload!.questions.length - 1];
            const { answerContext, questionId } = this.payload!.chatActionPayload;

            if (lastQuestion.answerContext !== answerContext) {
                this.addEmployeeMessage(this.label('chat.no-comment'));
                await this.submitSkipComment(questionId!);

                return;
            }

            const existingComment = lastQuestion.comment;

            this.addEmployeeMessage(this.label('chat.cancel'));

            if (existingComment) {
                await this.submitComment(questionId!, existingComment);
            } else {
                await this.submitSkipComment(questionId!);
            }
        });
    }

    @action
    onFreeTextUpdate(): void {
        this.refreshChat(async (): Promise<void> => {
            const existingComment = !this.payload!.questions.length ? undefined : this.payload!.questions[this.payload!.questions.length - 1].comment;

            if (existingComment !== undefined) {
                this.variables.deliveredMessages.pop();
            }
            this.variables.deliveredMessages.pop();

            const text = (this.variables.action! as FreeTextAction).value.trim();

            if (text !== '') {
                this.addEmployeeMessage(text);
                await this.submitCommentUpdate(this.payload!.chatActionPayload.questionId!, text);
            } else {
                this.addEmployeeMessage(this.label('chat.no-comment'));
                await this.submitCommentUpdate(this.payload!.chatActionPayload.questionId!, text);
            }
        });
    }

    @action
    onFreeTextUpdateCancel(): void {
        this.refreshChat(async (): Promise<void> => {
            const existingComment = !this.payload!.questions.length ? undefined : this.payload!.questions[this.payload!.questions.length - 1].comment;

            this.variables.deliveredMessages.pop();

            if (existingComment !== undefined) {
                await this.submitCommentUpdate(this.payload!.chatActionPayload.questionId!, existingComment);
            } else {
                this.addEmployeeMessage(this.label('chat.cancel'));
                await this.submitCommentUpdate(this.payload!.chatActionPayload.questionId!, '');
            }
        });
    }

    @action
    private deliverNextMessage(): void {
        const firstMessage = this.variables.preparedMessages.shift()!;

        if (firstMessage !== undefined) {
            this.variables.deliveredMessages.push(firstMessage);
        }
    }

    get botTyping(): boolean {
        return this.variables.preparedMessages.length !== 0;
    }

    private getEmployeeMessageLabel(context: AnswerContext | undefined, answer: string): string | undefined{
        switch (context) {
            case 'FOUR_OPTIONS':
                return this.label(`chat.four-choice.${answer}`);
            case 'BINARY':
                return this.label(`chat.binary.${answer}`);
            case 'ONLY_FREETEXT':
                return;
            default:
                return this.label(`chat.${answer}`);
        }
    }

    private getMessageColor(answer: string): 'red' | 'green' | 'white' {
        if (answer === '5' || answer === '4') {
            return 'green';
        } else if (answer === '1' || answer === '2') {
            return 'red';
        } else {
            return 'white';
        }
    }

    private getRedirectUrl(): string {
        const url = new URL(window.location.href);

        if (url.searchParams.has('token')) {
            url.searchParams.delete('token');
        }

        return url.toString();
    }

    private loadChat(employeeId: string): Promise<ChatPayload> {
        return this.api.get<ChatPayload>(`/chat/history/employee/${employeeId}`);
    }

    private loadQuestionChat(questionId: string, isAnsweringUnanswered?: boolean): Promise<ChatPayload> {
        const queryParameters: Record<string, string> = {};

        if (isAnsweringUnanswered === true) {
            queryParameters['answer-unanswered'] = 'true';
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        return this.api.get<ChatPayload>(`/chat/question/${questionId}?${queryParamsString}`);
    }

    private loadOnboarding(employeeId: string): Promise<ChatPayload> {
        return this.api.get<ChatPayload>(`/chat/onboarding/employee/${employeeId}`);
    }

    private loadWeeklyQuestionsChat(employeeId: string): Promise<ChatPayload> {
        return this.api.get<ChatPayload>(`/chat/weekly-questions/employee/${employeeId}`);
    }

    private submitAnswer(questionId: string, answer: string, context?: AnswerContext): Promise<void> {
        const queryParameters: Record<string, string> = {};

        if (context !== undefined) {
            queryParameters['answer-context'] = context;
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        return this.api.post<void>(`/chat/action/question/${questionId}/answer/${answer}?${queryParamsString}`);
    }

    private submitSkipComment(questionId: string): Promise<void> {
        return this.api.post<void>(`/chat/action/question/${questionId}/comment/skip`);
    }

    private submitComment(questionId: string, text: string): Promise<void> {
        return this.api.post<void>(`/chat/action/question/${questionId}/comment/submit`, {text});
    }

    private submitCommentUpdate(questionId: string, text: string): Promise<void> {
        return this.api.post<void>(`/chat/action/question/${questionId}/comment/update`, {text});
    }

    private initiateLogin(token: string | undefined, tenantId: string | undefined, loadPayload: () => Promise<void>): void {
        if (token !== undefined || tenantId !== undefined) {
            this.login(token, tenantId, loadPayload);
        } else {
            this.load(async () => {
                this.variables = new ChatVariables();
                let tokenToBeChecked;

                if (Capacitor.getPlatform() === 'web') {
                    tokenToBeChecked = sessionStorage.getItem('session-token') !== undefined || sessionStorage.getItem('session-token') !== null ?
                        sessionStorage.getItem('session-token') : undefined;
                } else {
                    tokenToBeChecked = localStorage.getItem('session-token') !== undefined || localStorage.getItem('session-token') !== null ?
                        localStorage.getItem('session-token') : undefined;
                }

                if (tokenToBeChecked === undefined || tokenToBeChecked === null || SessionStore.isExpired(SessionStore.parseJwtPayload(tokenToBeChecked))) {
                    this.variables.expired = true;

                    return;
                }
                await loadPayload();
                await this.userStore.loadUser();
                this.initializeVariables();
            });
        }
    }

    private login(token: string | undefined, tenantId: string | undefined, loadPayload: () => Promise<void>): void {
        this.load(async () => {
            this.variables = new ChatVariables();
            if (token === undefined || SessionStore.isExpired(SessionStore.parseJwtPayload(token))) {
                this.variables.expired = true;

                return;
            }
            await this.sessionStore.externalTokenLogin(token, tenantId);
            await loadPayload();
            await this.userStore.loadUser();
            this.initializeVariables();
        });
    }

    private initializeVariables(): void {
        this.addQuestionMessage();
        this.addOnboardingIfNeeded();
        this.preparePayloadAction();
        this.deliverMessagesWithTimer();
    }

    private refreshVariables(): void {
        this.preparePayloadAction();
        this.deliverMessagesWithTimer();
    }

    private preparePayloadAction(): void {
        this.variables.action = undefined;
        const amountOfQuestions = this.payload!.chatActionPayload.amountOfUnansweredQuestions;
        const questionType = this.payload!.chatActionPayload.questionType;
        const questionChannel = this.payload!.chatActionPayload.questionChannel;
        const chatContext = this.payload!.context;
        const weeklyQuestionsFlow = chatContext === 'weekly_questions' || this.userStore.payload?.employeeQuestionsFrequency === 'weekly';
        const notLastQuestionInFlow = amountOfQuestions !== undefined && amountOfQuestions > 0;
        const existingComment = !this.payload!.questions.length ? undefined : this.payload!.questions[this.payload!.questions.length - 1].comment;

        switch (this.payload!.chatActionPayload.action) {
            case 'ASK_QUESTION':
                if (amountOfQuestions !== undefined) {
                    this.getNumberOfQuestions(amountOfQuestions, this.payload!.chatActionPayload.isAnsweringUnanswered);
                }
                if (questionType === 'FEEDBACK_QUESTION') {
                    this.addBotMessage(this.label('chat.feedback-question.question-notification'));
                }
                this.addBotMessage(this.payload!.chatActionPayload.actionText!);
                this.addQuestionAction();
                break;
            case 'ASK_COMMENT':
                if (questionType === 'FEEDBACK_QUESTION' && questionChannel !== 'PUSH_NOTIFICATION' && chatContext !== 'weekly_questions' && this.payload!.chatActionPayload.isAnsweringUnanswered !== true) {
                    this.addBotMessage(this.label('chat.feedback-question.comment-notification'));
                }
                if ((weeklyQuestionsFlow && notLastQuestionInFlow) || (this.payload!.chatActionPayload.isAnsweringUnanswered && notLastQuestionInFlow)) {
                    this.addBotMessage(this.label('chat.comment.ask-comment'));
                    this.addFreeTextAction();
                } else {
                    if (existingComment !== undefined) {
                        this.addBotMessage(this.label('chat.comment.update'));
                        this.addFreeTextUpdateAction();
                    } else {
                        if (notLastQuestionInFlow) {
                            this.addBotMessage(this.label('chat.comment-or-unanswered.ask'));
                            this.addCommentOrAnswerUnansweredQuestionsAction();
                        } else {
                            this.addBotMessage(this.label('chat.comment-or-dashboard.ask'));
                            this.addCommentOrDashboardAction();
                        }
                    }
                }
                break;
            case 'UPDATE_COMMENT':
                this.addBotMessage(this.label('chat.comment.update'));
                this.addFreeTextUpdateAction();
                break;
            case 'ASK_ONBOARDING_CONSENT':
                this.addBotMessage(this.label('chat.new-hire'));
                this.addAskOnboardingConsentAction();
                break;
            case 'ASK_LANGUAGE':
                this.addBotMessage(this.label('chat.choose-language.ask'));
                this.addChooseLanguageAction();
                break;
            case 'ASK_DELIVERY_TIME':
                this.addBotMessage(this.label('chat.choose-delivery-time.ask'));
                this.addChooseDeliveryTimeAction();
                break;
            case 'ASK_CHANNEL':
                this.addBotMessage(this.label('chat.choose-channel.ask'));
                this.addChooseChannelAction();
                break;
            case 'ASK_QUESTION_FREQUENCY':
                this.addBotMessage(this.label('chat.choose-question-frequency.ask'));
                this.addChooseQuestionFrequencyAction();
                break;
            case 'ASK_ANSWER_CONTEXT':
                this.addBotMessage(this.label('chat.choose-answer-context.ask'));
                this.addChooseAnswerContextAction();
                break;
            default:
                this.addBotMessage(this.label(`chat.goodbye.${this.payload!.context}`));

                if (this.payload!.context === 'onboarding') {
                    this.addBotMessage(this.label('chat.onboarding-videos'), undefined, undefined, true);
                    break;
                } else {
                    if (this.isLastAndFirstDayOfMonth()) {
                        this.addBotMessage(this.label('chat.advertise-new-features'), undefined, undefined, true);
                        break;
                    } else {
                        break;
                    }
                }
        }
    }

    private addQuestionMessage(): void {
        this.payload!.questions.forEach((question) => {
            this.addBotMessage(question.text, question.date, question.stats);
            this.addEmployeeMessage(this.getEmployeeMessageLabel(question.answerContext, question.answer), this.getMessageColor(question.answer), question.date);
            if (question.comment !== undefined) {
                this.addEmployeeMessage(question.comment, 'white', question.date);
            }
            if (this.payload!.questions.length > 0 && this.payload!.questions[this.payload!.questions.length - 1].text === question.text && this.payload!.context === 'question' && this.payload!.chatActionPayload.questionType !== 'ADHOC_QUESTION') {
                this.addReaction(question.answer);
            }
        });
    }

    private addQuestionAction(): void {
        const type = 'ASK_QUESTION';
        const fiveChoices: ActionChoice[] = [
            {text: this.label('chat.1'), value: '1', color: 'red', questionAnswerContext: 'FIVE_OPTIONS'},
            {text: this.label('chat.2'), value: '2', color: 'red', questionAnswerContext: 'FIVE_OPTIONS'},
            {text: this.label('chat.3'), value: '3', color: 'white', questionAnswerContext: 'FIVE_OPTIONS'},
            {text: this.label('chat.4'), value: '4', color: 'green', questionAnswerContext: 'FIVE_OPTIONS'},
            {text: this.label('chat.5'), value: '5', color: 'green', questionAnswerContext: 'FIVE_OPTIONS'},
        ];
        const fourChoices: ActionChoice[] = [
            {text: this.label('chat.four-choice.5'), value: '5', color: 'green', questionAnswerContext: 'FOUR_OPTIONS'},
            {text: this.label('chat.four-choice.4'), value: '4', color: 'green-border', questionAnswerContext: 'FOUR_OPTIONS'},
            {text: this.label('chat.four-choice.2'), value: '2', color: 'red-border', questionAnswerContext: 'FOUR_OPTIONS'},
            {text: this.label('chat.four-choice.1'), value: '1', color: 'red', questionAnswerContext: 'FOUR_OPTIONS'},
        ];
        const binaryChoices: ActionChoice[] = [
            {text: this.label('chat.binary.5'), value: '5', color: 'green', questionAnswerContext: 'BINARY'},
            {text: this.label('chat.binary.1'), value: '1', color: 'red', questionAnswerContext: 'BINARY'},
        ];

        switch (this.payload!.chatActionPayload.answerContext) {
            case 'FIVE_OPTIONS':
                this.variables.action = new FiveChoiceAction(fiveChoices, type);
                break;
            case 'FOUR_OPTIONS':
                this.variables.action = new MultipleChoiceAction(fourChoices, type);
                break;
            case 'ONLY_FREETEXT':
                this.addFreeTextAction();
                break;
            default:
                this.variables.action = new MultipleChoiceAction(binaryChoices, type);
                break;
        }
    }

    private addCommentOrDashboardAction(): void {
        const type = 'ASK_COMMENT';
        const choices: ActionChoice[] = [
            {text: this.label('chat.option.comment'), value: 'comment', color: 'green'},
            {text: this.label('chat.option.dashboard'), value: 'dashboard', color: 'green'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private addCommentOrAnswerUnansweredQuestionsAction(): void {
        const type = 'ASK_COMMENT';
        const choices: ActionChoice[] = [
            {text: this.label('chat.option.comment'), value: 'comment', color: 'green'},
            {text: this.label('chat.option.answer-unanswered'), value: 'unanswered', color: 'green'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private routeToDashboard(): void {
        this.router.goTo(routes.dashboard);
        this.submitSkipComment(this.payload!.chatActionPayload.questionId!);
    }

    private addFreeTextAction(): void {
        const freeText = '';
        const type = 'WRITE_COMMENT';
        const choices: ActionChoice[] = [
            {text: this.label('chat.cancel'), value: 'cancel', color: 'white'},
            {text: this.label('chat.submit'), value: 'submit', color: 'green'},
        ];

        this.variables.action = new FreeTextAction(freeText, choices, type);
    }

    private addFreeTextUpdateAction(): void {
        const existingComment = !this.payload!.questions.length ? undefined : this.payload!.questions[this.payload!.questions.length - 1].comment;
        const freeText = existingComment !== undefined ? existingComment : '';
        const type = 'UPDATE_COMMENT';
        const choices: ActionChoice[] = [
            {text: this.label('chat.cancel'), value: 'cancel', color: 'white'},
            {text: this.label('chat.submit'), value: 'submit', color: 'green'},
        ];

        this.variables.action = new FreeTextAction(freeText, choices, type);
    }

    private addChooseLanguageAction(): void {
        const type = 'ASK_LANGUAGE';
        const companyLanguages = this.userStore.payload?.companyLanguages;
        const choices: ActionChoice[] = [];

        if (companyLanguages !== undefined) {
            if (companyLanguages.includes('en')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'en'),
                    value: 'en',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('et')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'et'),
                    value: 'et',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('fi')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'fi'),
                    value: 'fi',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('lt')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'lt'),
                    value: 'lt',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('lv')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'lv'),
                    value: 'lv',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('pl')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'pl'),
                    value: 'pl',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('ru')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'ru'),
                    value: 'ru',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('sv')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'sv'),
                    value: 'sv',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('vi')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'vi'),
                    value: 'vi',
                    color: 'green',
                });
            }
            if (companyLanguages.includes('ja')) {
                choices.push({
                    text: this.labelStore.translate('chat.choose-language.language', 'ja'),
                    value: 'ja',
                    color: 'green',
                });
            }
            this.variables.action = new MultipleChoiceAction(choices, type);
        }
    }

    private addChooseQuestionFrequencyAction(): void {
        const type = 'ASK_QUESTION_FREQUENCY';
        const choices: ActionChoice[] = [
            {text: this.label('chat.choose-question-frequency.weekly'), value: 'weekly', color: 'green'},
            {text: this.label('chat.choose-question-frequency.one_every_day'), value: 'one_every_day', color: 'green'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private addChooseAnswerContextAction(): void {
        const type = 'ASK_ANSWER_CONTEXT';
        const companyAnswerContext = this.userStore.payload?.companyAnswerContext;
        const choices: ActionChoice[] = [];

        if (companyAnswerContext !== undefined) {
            if (companyAnswerContext.includes('BINARY')) {
                choices.push({text: this.label('common.answer-context.BINARY'), value: 'BINARY', color: 'green'});
            }
            if (companyAnswerContext.includes('FOUR_OPTIONS')) {
                choices.push({text: this.label('common.answer-context.FOUR_OPTIONS'), value: 'FOUR_OPTIONS', color: 'green'});
            }
            if (companyAnswerContext.includes('FIVE_OPTIONS')) {
                choices.push({text: this.label('common.answer-context.FIVE_OPTIONS'), value: 'FIVE_OPTIONS', color: 'green'});
            }
            this.variables.action = new MultipleChoiceAction(choices, type);
        }
    }

    private addChooseDeliveryTimeAction(): void {
        const type = 'ASK_DELIVERY_TIME';
        const choices: ActionChoice[] = [
            {text: this.label('chat.choose-delivery-time.morning'), value: 'morning', color: 'green'},
            {text: this.label('chat.choose-delivery-time.afternoon'), value: 'afternoon', color: 'green'},
            {text: this.label('chat.choose-delivery-time.evening'), value: 'evening', color: 'green'},
            {text: this.label('chat.choose-delivery-time.latenight'), value: 'latenight', color: 'green'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private addChooseChannelAction(): void {
        const type = 'ASK_CHANNEL';
        let choices: ActionChoice[] = [];
        const channelOptions = this.payload!.chatActionPayload.channels;

        if (channelOptions?.includes('mobile')) {
            choices = choices.concat(this.addMobileAppDownloadOptions());
        }

        const nonMobileOptions = channelOptions?.filter((option) => option !== 'mobile');

        if (nonMobileOptions !== undefined) {
            for (const oneChannel of nonMobileOptions) {
                choices.push({
                    text: this.label(`chat.choose-channel.${oneChannel}`),
                    value: oneChannel,
                    color: 'green',
                });
            }
        }

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private addMobileAppDownloadOptions(): ActionChoice[] {
        return [
            {text: this.label('chat.choose-channel.android'), value: 'android', color: 'green'},
            {text: this.label('chat.choose-channel.iphone'), value: 'iphone', color: 'green'},
        ];
    }

    private addAskOnboardingConsentAction(): void {
        const type = 'ASK_ONBOARDING_CONSENT';
        const choices: ActionChoice[] = [
            {text: this.label('onboarding.yes'), value: 'yes', color: 'green'},
            {text: this.label('onboarding.no'), value: 'no', color: 'red'},
        ];

        this.variables.action = new MultipleChoiceAction(choices, type);
    }

    private addReaction(answer: string): void {
        const randomNumber = Math.floor(Math.random() * 4) + 1;

        if (answer === '5' || answer === '4') {
            this.addBotMessage(this.label(`chat.positive.reaction.${randomNumber}`));
        } else {
            this.addBotMessage(this.label(`chat.negative.reaction.${randomNumber}`));
        }
    }

    private getNumberOfQuestions(amountOfQuestions: number, isAnsweringUnanswered?: boolean): void {
        if (amountOfQuestions === 1) {
            this.addBotMessage(isAnsweringUnanswered === true ? this.label('chat.unanswered-questions.last-question', {number_of_questions: amountOfQuestions.toString()}) : this.label('chat.weekly-questions.last-question', {number_of_questions: amountOfQuestions.toString()}));
        } else {
            this.addBotMessage(isAnsweringUnanswered === true ? this.label('chat.unanswered-questions.question-number', {number_of_questions: amountOfQuestions.toString()}) : this.label('chat.weekly-questions.question-number', {number_of_questions: amountOfQuestions.toString()}));
        }
    }

    private isLastAndFirstDayOfMonth(): boolean {
        return startOfMonth || endOfMonth;
    }

    private deliverMessagesWithTimer(): void {
        if (this.variables.preparedMessages.length !== 0 && this.variables.deliveredMessages.length === 0) {
            this.deliverNextMessage();
        }
        while (this.variables.preparedMessages.some((message) => message.party === 'employee')) {
            this.deliverNextMessage();
        }
        this.startNextMessageTimer();
    }

    private startNextMessageTimer(): void {
        if (this.variables.preparedMessages.length !== 0) {
            setTimeout(() => {
                this.deliverNextMessage();
                this.startNextMessageTimer();
            }, this.variables.preparedMessages[0].text.length * 10 + 500);
        }
    }

    private addEmployeeMessage(text?: string, color: 'red' | 'green' | 'white' = 'white', date: string = todayAsISOString()): void {
        if (text !== undefined) {
            this.variables.preparedMessages.push({text, party: 'employee', date, color});
        }
    }

    private addBotMessage(text: string, date: string = todayAsISOString(), questionStats?: Datapoint, hasClickableUrl?: boolean): void {
        this.variables.preparedMessages.push({text, party: 'bot', date, color: 'white', questionStats, hasClickableUrl});
    }

    private addOnboardingIfNeeded(): void {
        if (this.payload!.context === 'onboarding') {
            let messageCounter = 1;

            while (this.labelStore.hasLabel(`chat.onboarding.${messageCounter}`)) {
                this.addBotMessage(
                    this.label(`chat.onboarding.${messageCounter}`, {
                        first_name: this.payload!.employeeFirstName,
                        company_name: this.payload!.companyName,
                        department_name: this.payload!.businessUnitName,
                    }),
                );
                messageCounter++;
            }
        }
    }

    private async refreshChat(processAction: () => Promise<void>): Promise<void> {

        await processAction();

        switch (this.payload!.context) {
            case 'onboarding':
                this.payload = await this.loadOnboarding(this.payload!.employeeId);
                break;
            case 'question':
                this.payload = await this.loadQuestionChat(this.payload!.chatActionPayload.questionId!, this.payload!.chatActionPayload.isAnsweringUnanswered);
                break;
            case 'history':
                this.payload = await this.loadChat(this.payload!.employeeId);
                break;
            case 'weekly_questions':
                this.payload = await this.loadWeeklyQuestionsChat(this.payload!.employeeId);
                break;
            default:
                throw new Error(`unsupported context ${this.payload!.context}`);
        }

        this.refreshVariables();
    }
}

type ChatContext = 'question' | 'history' | 'onboarding' | 'weekly_questions';
type QuestionChannel = 'PUSH_NOTIFICATION' | 'EMAIL' | 'AUTO_ANSWER' | 'STATION';
type QuestionType = 'FEEDBACK_QUESTION' | 'ADHOC_QUESTION' | 'CATALOG_QUESTION' | 'ONBOARDING_QUESTION';
type ActionType =
    'ASK_QUESTION'
    | 'ASK_COMMENT'
    | 'UPDATE_COMMENT'
    | 'WRITE_COMMENT'
    | 'ASK_LANGUAGE'
    | 'ASK_DELIVERY_TIME'
    | 'ASK_CHANNEL'
    | 'ASK_ONBOARDING_CONSENT'
    | 'ASK_QUESTION_FREQUENCY'
    | 'ASK_ANSWER_CONTEXT';

interface ChatPayload {
    employeeFirstName: string;
    companyName: string;
    businessUnitName: string;
    questions: ChatQuestion[];
    context: ChatContext;
    employeeId: string;
    chatActionPayload: ChatActionPayload;
}

interface ChatActionPayload {
    action?: ActionType;
    actionText?: string;
    questionId?: string;
    questionType?: QuestionType;
    questionChannel?: QuestionChannel;
    amountOfUnansweredQuestions?: number;
    channels?: Channel[];
    answerContext?: AnswerContext;
    isAnsweringUnanswered?: boolean;
}

interface ChatQuestion {
    text: string;
    answer: '5' | '4' | '3' | '2' | '1';
    answerContext: AnswerContext;
    comment?: string;
    date: string;
    stats?: Datapoint;
}

export class ChatVariables {
    @observable
    preparedMessages: ChatMessage[];
    @observable
    deliveredMessages: ChatMessage[];
    @observable
    action?: ChatAction;
    @observable
    expired: boolean;

    constructor(preparedMessages: ChatMessage[] = [], deliveredMessages: ChatMessage[] = [], chatAction?: ChatAction, expired: boolean = false) {
        this.preparedMessages = preparedMessages;
        this.deliveredMessages = deliveredMessages;
        this.action = chatAction;
        this.expired = expired;
        makeObservable(this);
    }
}

export class ChatMessage {
    text: string;
    party: 'employee' | 'bot';
    date: string;
    color: 'red' | 'green' | 'white';
    questionStats?: Datapoint;
    hasClickableUrl?: boolean = false;

    constructor(text: string, party: 'employee' | 'bot', date: string, color: 'red' | 'green' | 'white') {
        this.text = text;
        this.party = party;
        this.date = date;
        this.color = color;
    }
}

export interface ChatAction {
    type: ActionType;
}

export class MultipleChoiceAction implements ChatAction {
    choices: ActionChoice[];
    type: ActionType;

    constructor(choices: ActionChoice[], type: ActionType) {
        this.choices = choices;
        this.type = type;
    }
}

class ActionChoice {
    text: string;
    value: string;
    color: 'green' | 'red' | 'white' | 'red-border' | 'green-border';
    questionAnswerContext?: AnswerContext = undefined;

    constructor(text: string, value: string, color: 'green' | 'red' | 'white'| 'red-border' | 'green-border', questionAnswerContext: AnswerContext) {
        this.text = text;
        this.value = value;
        this.color = color;
        this.questionAnswerContext = questionAnswerContext;
    }
}

export class FiveChoiceAction implements ChatAction {
    choices: ActionChoice[];
    type: ActionType;

    constructor(choices: ActionChoice[], type: ActionType) {
        this.choices = choices;
        this.type = type;
    }
}

export class FreeTextAction implements ChatAction {
    @observable
    value: string;
    choices: ActionChoice[];
    type: ActionType;

    constructor(value: string, choices: ActionChoice[], type: ActionType) {
        this.value = value;
        this.choices = choices;
        this.type = type;
        makeObservable(this);
    }
}
